import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { downloadFile, errorMessage } from '../../api/client';
import { fetchDealerReceiptImageUrl, useAddDealerReceipt, useDealerLedger, useDealerReceipts } from '../../api/hooks';
import { useAuth } from '../../context/AuthContext';
import type { DealerProfitReceipt } from '../../types';
import { apiDate, date, money } from '../../utils/format';
import { maxAmountRule } from '../../utils/maxAmountRule';
import { paymentMethodOptions } from '../../utils/paymentMethods';
import PendingDocumentPicker from '../investor/PendingDocumentPicker';
import TransactionImageLink from '../investor/TransactionImageLink';
import TransactionAccountField from '../common/TransactionAccountField';
import { useTransactionAccountField } from '../../utils/transactionAccountField';
import QuickShareButton from '../common/QuickShareButton';
import { usePartyContact } from '../common/usePartyContact';
import { transactionMessage } from '../../utils/quickShare';
import { useTranslation } from 'react-i18next';
import AutoText from '../common/AutoText';

/** Mirrors the investor Profit tab: accrued/received/outstanding cards plus the payment history. */
export default function DealerProfitTab({ dealerId }: { dealerId: number }) {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const canWrite = hasRole('Admin');
  const { data: ledger, isLoading } = useDealerLedger(dealerId);
  const { data: receipts } = useDealerReceipts(dealerId);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [transactionImage, setTransactionImage] = useState<File | null>(null);
  const addReceipt = useAddDealerReceipt(dealerId);
  const accountField = useTransactionAccountField('Dealer', dealerId);
  const contact = usePartyContact('Dealer', dealerId);
  const paymentMethod = Form.useWatch('paymentMethod', form) as string | undefined;

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await addReceipt.mutateAsync({
        amount: values.amount as number,
        receiptDate: apiDate(values.receiptDate as dayjs.Dayjs)!,
        paymentMethod: values.paymentMethod as string | undefined,
        notes: values.notes as string | undefined,
        transactionImage,
      });
      await accountField.commitIfChanged();
      message.success('Profit payment recorded.');
      form.resetFields();
      setTransactionImage(null);
      setOpen(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.setFieldsValue({ receiptDate: dayjs() });
              setOpen(true);
            }}
          >
            Profit Withdrawn
          </Button>
        )}
        <Button
          icon={<DownloadOutlined />}
          onClick={() =>
            downloadFile(`/reports/dealer-profit-statement/${dealerId}`, `profit_statement_${dealerId}.pdf`).catch(
              (err) => message.error(errorMessage(err)),
            )
          }
        >
          Download profit statement (PDF)
        </Button>
      </Space>

      {isLoading || !ledger ? (
        <Skeleton active />
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card>
              <Statistic title={t('finance.totalProfit')} value={money(ledger.totalAccruedProfit)} prefix="PKR" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic title="Paid so far" value={money(ledger.totalReceivedProfit)} prefix="PKR" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Statistic
                title={t('finance.remainingAmount')}
                value={money(ledger.outstandingProfit)}
                prefix="PKR"
                valueStyle={{ color: ledger.outstandingProfit > 0 ? '#FF0000' : '#3EB780' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card title={t('dashboard.paymentHistory')} style={{ marginTop: 16 }} size="small">
        <Table<DealerProfitReceipt>
          rowKey="id"
          size="small"
          dataSource={receipts ?? []}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: t('common.date'), dataIndex: 'receiptDate', render: date },
            { title: t('common.amount'), dataIndex: 'amount', align: 'right', render: money },
            { title: t('table.method'), dataIndex: 'paymentMethod', render: (v: string | null) => v ?? '—' },
            { title: t('common.notes'), dataIndex: 'notes', render: (v: string | null) => <AutoText value={v} /> },
            {
              title: t('finance.transactionProof'),
              render: (_, row) => (
                <TransactionImageLink
                  exists={row.hasTransactionImage}
                  title={`Transaction proof — payment #${row.id}`}
                  fetchUrl={() => fetchDealerReceiptImageUrl(dealerId, row.id)}
                />
              ),
            },
            {
              title: t('finance.receipt'),
              render: (_, row) => (
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    downloadFile(
                      `/receipts/dealer-profit-payment/${row.id}`,
                      `profit_payment_receipt_${row.id}.pdf`,
                    ).catch((err) => message.error(errorMessage(err)))
                  }
                >
                  PDF
                </Button>
              ),
            },
            ...(contact.ready
              ? [
                  {
                    title: t('common.share'),
                    render: (_: unknown, row: DealerProfitReceipt) => (
                      <QuickShareButton
                        phone={contact.phone}
                        recipientName={contact.name}
                        message={transactionMessage(contact, 'Profit payment', row.amount, row.receiptDate)}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Modal maskClosable={false}
        title="Record profit payment"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={addReceipt.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
          <Form.Item
            name="amount"
            label={t('finance.amountPkr')}
            rules={[
              { required: true },
              { type: 'number', min: 0.01, message: 'Amount must be greater than zero.' },
              maxAmountRule(ledger?.outstandingProfit, 'remaining profit'),
            ]}
          >
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="receiptDate" label={t('finance.paymentDate')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} disabledDate={(d) => d.isAfter(dayjs(), 'day')} />
          </Form.Item>
          <Form.Item name="paymentMethod" label={t('finance.paymentMethod')}>
            <Select options={paymentMethodOptions} allowClear />
          </Form.Item>
          <TransactionAccountField field={accountField} paymentMethod={paymentMethod} />
          <Form.Item name="notes" label={t('common.notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>

          <PendingDocumentPicker
            type="profit-payment-image"
            label={t('finance.transactionProofOptional')}
            hint="Receipt or transfer screenshot proving the profit was paid · JPG, PNG or WebP · max 5 MB"
            file={transactionImage}
            onChange={setTransactionImage}
          />
        </Form>
      </Modal>
    </>
  );
}
